import React, { useState } from "react";
import logo from "../../resource/7.png";
import useFormHandler from "../../Hook/useFormHandler";

const OrdinationRegistration = () => {
  const initialValues = {
    // Personal Information
    first_name: "",
    last_name: "",
    date_of_birth: "",
    age: "",
    gender: "",
    address: "",
    city: "",
    postal_code: "",
    mobile_number: "",
    whatsapp_number: "",
    email_address: "",
    marital_status: "",
    has_permission: "",

    // Background Check
    military_service: "",
    criminal_record: "",

    // Ordination Details
    ordination_type: "",
    ordination_time: "",
    ordination_month: "",
    ordination_year: "",

    // Emergency Contact
    emergency_first_name: "",
    emergency_last_name: "",
    emergency_email: "",
    emergency_relationship: "",
    emergency_mobile_1: "",
    emergency_mobile_2: "",

    // Medical History
    has_mental_disorder_history: "",
    has_contagious_disease: "",
    other_health_complications: "",

    // Additional Information
    queries: "",

    ip_address: ""
  };

  const validations = {
    // Personal Information
    first_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "First Name should contain only letters, spaces, or hyphens",
    },
    last_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "Last Name should contain only letters, spaces, or hyphens",
    },
    date_of_birth: {
      required: true,
      pattern: /^\d{4}-\d{2}-\d{2}$/,
      message: "Date of Birth must be in YYYY-MM-DD format",
    },
    age: {
      required: true,
      pattern: /^\d+$/,
      message: "Age must be a valid number",
      validate: (value) => {
        const numValue = Number(value);
        return (numValue > 0 && numValue < 120) || "Age must be between 1 and 119";
      },
    },
    gender: {
      required: true,
      validate: (value) =>
        ["male", "female", "other"].includes(value) || "Gender must be Male, Female, or Other",
    },
    address: {
      required: true,
      minLength: 5,
      message: "Address must be at least 5 characters long",
    },
    city: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "City should contain only letters, spaces, or hyphens",
    },
    postal_code: {
      required: true,
      pattern: /^\d{5,10}$/,
      message: "Postal Code should be 5 to 10 digits",
    },
    mobile_number: {
      required: true,
      pattern: /^\+?\d{10,15}$/,
      message: "Mobile Number should be 10 to 15 digits, with optional '+'",
    },
    whatsapp_number: {
      required: true,
      pattern: /^\+?\d{10,15}$/,
      message: "WhatsApp Number should be 10 to 15 digits, with optional '+'",
    },
    email_address: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Invalid email format",
    },
    marital_status: {
      required: true,
      validate: (value) =>
        ["single", "married", "divorced", "widowed"].includes(value) ||
        "Invalid marital status",
    },
    has_permission: {
      required: true,
      validate: (value) => value !== "" || "Please select an option",
    },

    // Background Check
    military_service: {
      required: true,
      validate: (value) => value !== "" || "Please select an option",
    },
    criminal_record: {
      required: true,
      validate: (value) => value !== "" || "Please select an option",
    },

    // Ordination Details
    ordination_type: {
      required: true,
      validate: (value) =>
        ["short_term", "permanent"].includes(value) || "Please select a valid ordination type",
    },
    ordination_month: {
      required: true,
      validate: (value) =>
        [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December",
        ].includes(value) || "Please select a valid month",
    },
    ordination_year: {
      required: true,
      pattern: /^\d{4}$/,
      message: "Year must be a 4-digit number",
      validate: (value) => {
        const year = Number(value);
        const currentYear = new Date().getFullYear();
        return (
          year >= currentYear && year <= currentYear + 10
        ) || `Year must be between ${currentYear} and ${currentYear + 10}`;
      },
    },

    // Emergency Contact
    emergency_first_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "Emergency Contact First Name should contain only letters, spaces, or hyphens",
    },
    emergency_last_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "Emergency Contact Last Name should contain only letters, spaces, or hyphens",
    },
    emergency_email: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Invalid email format",
    },
    emergency_relationship: {
      required: true,
      minLength: 2,
      message: "Relationship must be at least 2 characters",
    },
    emergency_mobile_1: {
      required: true,
      pattern: /^\+?\d{10,15}$/,
      message: "Emergency Mobile 1 should be 10 to 15 digits, with optional '+'",
    },
    emergency_mobile_2: {
      required: false,
      pattern: /^\+?\d{10,15}$/,
      message: "Emergency Mobile 2 should be 10 to 15 digits, with optional '+'",
    },

    // Medical History
    has_mental_disorder_history: {
      required: true,
      validate: (value) => value !== "" || "Please select an option",
    },
    has_contagious_disease: {
      required: true,
      validate: (value) => value !== "" || "Please select an option",
    },
    other_health_complications: {
      required: false,
      maxLength: 300,
      message: "Health complications must be under 300 characters",
    },

    // Additional Information
    queries: {
      required: false,
      maxLength: 500,
      message: "Queries cannot exceed 500 characters",
    },
  };

  const endpoint = '/create-ordination-registrations';

  const transformForApi = (data) => ({
    // Personal Information
    first_name: data.first_name,
    last_name: data.last_name,
    date_of_birth: data.date_of_birth,
    age: Number(data.age),
    gender: data.gender,
    address: data.address,
    city: data.city,
    postal_code: data.postal_code,
    mobile_number: data.mobile_number,
    whatsapp_number: data.whatsapp_number,
    email_address: data.email_address,
    marital_status: data.marital_status,
    has_permission: data.has_permission === "true",

    // Background Check
    military_service: data.military_service === "true",
    criminal_record: data.criminal_record === "true",

    // Ordination Details
    ordination_type: data.ordination_type,
    ordination_time: `${data.ordination_month} ${data.ordination_year}`,
    ordination_month: data.ordination_month,
    ordination_year: Number(data.ordination_year),

    // Emergency Contact
    emergency_first_name: data.emergency_first_name,
    emergency_last_name: data.emergency_last_name,
    emergency_email: data.emergency_email,
    emergency_relationship: data.emergency_relationship,
    emergency_mobile_1: data.emergency_mobile_1,
    emergency_mobile_2: data.emergency_mobile_2,

    // Medical History
    has_mental_disorder_history: data.has_mental_disorder_history === "true",
    has_contagious_disease: data.has_contagious_disease === "true",
    other_health_complications: data.other_health_complications,

    // Additional Information
    queries: data.queries,
    ip_address: data.ip_address,
  });

  const {
    formData,
    errors,
    loading,
    handleInputChange,
    handleSubmit,
    setFormData,
  } = useFormHandler({
    initialValues,
    validations,
    endpoint,
    includeIP: true,
    transformFn: transformForApi
  });

  return (
    <div className="form-page dark-gold">
      <form onSubmit={handleSubmit}>
        <div className="form-title">
          <img src={logo} alt="Monastery Logo" width={200} />
          <div className="details">
            <h1 className="font-second">Bhaddeka Vihari Monastery Center</h1>
            <h2 className="h4">Ordination Registration Form</h2>
          </div>
        </div>

        <p className="font-p mt-6">
          Welcome to Bhaddheka Vihari Monastery Ordination
        </p>
        <p className="font-p mt-4">
          Use the following form to fill in the layman's details
        </p>

        <p className="font-p mt-5">Full Name *</p>

        <div className="form-box mt-4">
          <div className="form-item">
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
            />
            <label htmlFor="first_name">First Name</label>
            {errors.first_name && <span className="error">{errors.first_name}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
            />
            <label htmlFor="last_name">Last Name</label>
            {errors.last_name && <span className="error">{errors.last_name}</span>}
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="date"
              id="date_of_birth"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleInputChange}
            />
            <label htmlFor="date_of_birth">Date of birth*</label>
            {errors.date_of_birth && <span className="error">{errors.date_of_birth}</span>}
          </div>
          <div className="form-item">
            <input
              type="number"
              id="age"
              name="age"
              value={formData.age}
              onChange={handleInputChange}
            />
            <label htmlFor="age">Age*</label>
            {errors.age && <span className="error">{errors.age}</span>}
          </div>
        </div>

        <p className="font-p mt-5">Gender*</p>
        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="male"
              name="gender"
              value="male"
              checked={formData.gender === "male"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="male">Male</label>
          </div>
          <div>
            <input
              type="radio"
              id="female"
              name="gender"
              value="female"
              checked={formData.gender === "female"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="female">Female</label>
          </div>
          
        </div>
        {errors.gender && <span className="error">{errors.gender}</span>}

        <p className="font-p mt-5">Address*</p>

        <div className="form-box1">
          <div className="form-item">
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
            />
            <label htmlFor="address">Address</label>
            {errors.address && <span className="error">{errors.address}</span>}
          </div>
        </div>

        <div className="form-box1 mt-5">
          <div className="form-item">
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
            />
            <label htmlFor="city">City</label>
            {errors.city && <span className="error">{errors.city}</span>}
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="text"
              id="postal_code"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleInputChange}
            />
            <label htmlFor="postal_code">Postal/Zip Code*</label>
            {errors.postal_code && <span className="error">{errors.postal_code}</span>}
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="text"
              id="mobile_number"
              name="mobile_number"
              value={formData.mobile_number}
              onChange={handleInputChange}
            />
            <label htmlFor="mobile_number">Mobile Number</label>
            {errors.mobile_number && <span className="error">{errors.mobile_number}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="whatsapp_number"
              name="whatsapp_number"
              value={formData.whatsapp_number}
              onChange={handleInputChange}
            />
            <label htmlFor="whatsapp_number">WhatsApp Number</label>
            {errors.whatsapp_number && <span className="error">{errors.whatsapp_number}</span>}
          </div>
        </div>

        <div className="form-box1 mt-5">
          <div className="form-item">
            <input
              type="email"
              id="email_address"
              name="email_address"
              value={formData.email_address}
              onChange={handleInputChange}
            />
            <label htmlFor="email_address">Email Address</label>
            {errors.email_address && <span className="error">{errors.email_address}</span>}
          </div>
        </div>

        <p className="font-p mt-5">Marital Status*</p>

        <div className="selection mt-4">
          <div className="gender-box">
            <div>
              <input
                type="radio"
                id="single"
                name="marital_status"
                value="single"
                checked={formData.marital_status === "single"}
                onChange={handleInputChange}
                className="mt-5"
              />
              <label htmlFor="single">Single</label>
            </div>
            <div>
              <input
                type="radio"
                id="married"
                name="marital_status"
                value="married"
                checked={formData.marital_status === "married"}
                onChange={handleInputChange}
                className="mt-5"
              />
              <label htmlFor="married">Married</label>
            </div>
            
           
          </div>
        </div>
        {errors.marital_status && <span className="error">{errors.marital_status}</span>}

        <p className="font-p mt-5">
          Please indicate if you have received permission from your spouse or
          parent or guardian for ordination*
        </p>

        <div className="selection mt-4">
          <div className="checkbox-box">
            <label htmlFor="has_permission_yes">Yes</label>
            <input
              type="radio"
              id="has_permission_yes"
              name="has_permission"
              value="true"
              checked={formData.has_permission === "true"}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="has_permission_no">No</label>
            <input
              type="radio"
              id="has_permission_no"
              name="has_permission"
              value="false"
              checked={formData.has_permission === "false"}
              onChange={handleInputChange}
            />
          </div>
        </div>
        {errors.has_permission && <span className="error">{errors.has_permission}</span>}

        <p className="font-p mt-5">
          Please indicate if you are currently serving the Army, Navy or Air
          Force of any country*
        </p>

        <div className="selection mt-4">
          <div className="checkbox-box">
            <label htmlFor="military_yes">Yes</label>
            <input
              type="radio"
              id="military_yes"
              name="military_service"
              value="true"
              checked={formData.military_service === "true"}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="military_no">No</label>
            <input
              type="radio"
              id="military_no"
              name="military_service"
              value="false"
              checked={formData.military_service === "false"}
              onChange={handleInputChange}
            />
          </div>
        </div>
        {errors.military_service && <span className="error">{errors.military_service}</span>}

        <p className="font-p mt-5">
          Please indicate whether any warrant for arrest or criminal conviction
          has been imposed against you.*
        </p>

        <div className="selection mt-4">
          <div className="checkbox-box">
            <label htmlFor="criminal_yes">Yes</label>
            <input
              type="radio"
              id="criminal_yes"
              name="criminal_record"
              value="true"
              checked={formData.criminal_record === "true"}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="criminal_no">No</label>
            <input
              type="radio"
              id="criminal_no"
              name="criminal_record"
              value="false"
              checked={formData.criminal_record === "false"}
              onChange={handleInputChange}
            />
          </div>
        </div>
        {errors.criminal_record && <span className="error">{errors.criminal_record}</span>}

        <h1 className="font-second mt-6">Ordination Details</h1>

        <p className="font-p mt-5">
          Please indicate if you wish to be ordained short term or permanently*
        </p>

        <div className="selection mt-4">
          <div className="checkbox-box">
            <label htmlFor="ordination_short">Short Term</label>
            <input
              type="radio"
              id="ordination_short"
              name="ordination_type"
              value="short_term"
              checked={formData.ordination_type === "short_term"}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="ordination_perm">Permanent</label>
            <input
              type="radio"
              id="ordination_perm"
              name="ordination_type"
              value="permanent"
              checked={formData.ordination_type === "permanent"}
              onChange={handleInputChange}
            />
          </div>
        </div>
        {errors.ordination_type && <span className="error">{errors.ordination_type}</span>}

        <p className="font-p mt-5">
          Please indicate the time you wish to get ordained*
        </p>

        <div className="selection mt-4">
          <div className="checkbox-box">
            <label htmlFor="ordination_month">Month*</label>
            <select
              id="ordination_month"
              name="ordination_month"
              value={formData.ordination_month}
              onChange={handleInputChange}
              style={{ padding: "6px", borderRadius: "4px" }}
            >
              <option value="">Select Month</option>
              <option value="January">January</option>
              <option value="February">February</option>
              <option value="March">March</option>
              <option value="April">April</option>
              <option value="May">May</option>
              <option value="June">June</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
              <option value="October">October</option>
              <option value="November">November</option>
              <option value="December">December</option>
            </select>
            {errors.ordination_month && <span className="error">{errors.ordination_month}</span>}
          </div>
          
          <div className="checkbox-box">
            <label htmlFor="ordination_year">Year*</label>
            <input
              type="number"
              id="ordination_year"
              name="ordination_year"
              value={formData.ordination_year}
              onChange={handleInputChange}
              min={new Date().getFullYear()}
              max={new Date().getFullYear() + 10}
              style={{ width: "80px", padding: "6px", borderRadius: "4px" }}
            />
            {errors.ordination_year && <span className="error">{errors.ordination_year}</span>}
          </div>
        </div>

        <h1 className="font-second mt-6">Emergency Contact</h1>

        <div className="form-box mt-4">
          <div className="form-item">
            <input
              type="text"
              id="emergency_first_name"
              name="emergency_first_name"
              value={formData.emergency_first_name}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_first_name">First Name*</label>
            {errors.emergency_first_name && <span className="error">{errors.emergency_first_name}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="emergency_last_name"
              name="emergency_last_name"
              value={formData.emergency_last_name}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_last_name">Last Name*</label>
            {errors.emergency_last_name && <span className="error">{errors.emergency_last_name}</span>}
          </div>
        </div>

        <div className="form-box mt-4">
          <div className="form-item">
            <input
              type="email"
              id="emergency_email"
              name="emergency_email"
              value={formData.emergency_email}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_email">Email Address*</label>
            {errors.emergency_email && <span className="error">{errors.emergency_email}</span>}
          </div>
        </div>

        <div className="form-box mt-4">
          <div className="form-item">
            <input
              type="text"
              id="emergency_relationship"
              name="emergency_relationship"
              value={formData.emergency_relationship}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_relationship">Relationship*</label>
            {errors.emergency_relationship && <span className="error">{errors.emergency_relationship}</span>}
          </div>
        </div>

        <div className="form-box mt-4">
          <div className="form-item">
            <input
              type="text"
              id="emergency_mobile_1"
              name="emergency_mobile_1"
              value={formData.emergency_mobile_1}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_mobile_1">Mobile Number 1*</label>
            {errors.emergency_mobile_1 && <span className="error">{errors.emergency_mobile_1}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="emergency_mobile_2"
              name="emergency_mobile_2"
              value={formData.emergency_mobile_2}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_mobile_2">Mobile Number 2 (Optional)</label>
            {errors.emergency_mobile_2 && <span className="error">{errors.emergency_mobile_2}</span>}
          </div>
        </div>

        <h1 className="font-second mt-6">Medical History*</h1>
        <p className="font-p mt-5">
          Kindly answer the following questions truthfully and accurately. You
          are required to answer all of the following questions.
        </p>

        <p className="font-p mt-4">
          Is there any past or present history of mental disorder or illness
          such as panic attack, anxiety, schizophrenia or manic-depression?*
        </p>

        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="mental_yes"
              name="has_mental_disorder_history"
              value="true"
              checked={formData.has_mental_disorder_history === "true"}
              onChange={handleInputChange}
            />
            <label htmlFor="mental_yes">Yes</label>
          </div>
          <div>
            <input
              type="radio"
              id="mental_no"
              name="has_mental_disorder_history"
              value="false"
              checked={formData.has_mental_disorder_history === "false"}
              onChange={handleInputChange}
            />
            <label htmlFor="mental_no">No</label>
          </div>
        </div>
        {errors.has_mental_disorder_history && <span className="error">{errors.has_mental_disorder_history}</span>}

        <p className="font-p mt-4">
          Do you suffer from any contagious diseases like TB and Hepatitis, or
          from any illness that requires constant treatment?*
        </p>

        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="contagious_yes"
              name="has_contagious_disease"
              value="true"
              checked={formData.has_contagious_disease === "true"}
              onChange={handleInputChange}
            />
            <label htmlFor="contagious_yes">Yes</label>
          </div>
          <div>
            <input
              type="radio"
              id="contagious_no"
              name="has_contagious_disease"
              value="false"
              checked={formData.has_contagious_disease === "false"}
              onChange={handleInputChange}
            />
            <label htmlFor="contagious_no">No</label>
          </div>
        </div>
        {errors.has_contagious_disease && <span className="error">{errors.has_contagious_disease}</span>}

        <p className="font-p mt-5">
          Please state any other current health complications and/or
          restrictions, physical or psychological that may and/or will affect
          you during your retreat
        </p>

        <div className="form-item mt-4">
          <textarea
            id="other_health_complications"
            name="other_health_complications"
            value={formData.other_health_complications}
            onChange={handleInputChange}
            rows="4"
            placeholder="Optional - describe any health complications..."
          />
          {errors.other_health_complications && <span className="error">{errors.other_health_complications}</span>}
        </div>

        <p className="font-p mt-5">
          Please write any specific questions or queries you would like to
          clarify getting ordained at the Monastery
        </p>

        <div className="form-item mt-4">
          <textarea
            id="queries"
            name="queries"
            value={formData.queries}
            onChange={handleInputChange}
            rows="4"
            placeholder="Optional - write any questions or queries..."
          />
          {errors.queries && <span className="error">{errors.queries}</span>}
        </div>

        <button
          type="submit"
          id="submitbtn"
          className="mt-5"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>

        <p className="font-p mt-5 pb-4">
          We look forward to facilitating your request for Ordination and are
          grateful for choosing Bhaddheka Vihari Monastery as an enriching space for
          practicing mindfulness meditation.
        </p>
      </form>
    </div>
  );
};

export default OrdinationRegistration;